import { IAccessRightProfile, PERSONA, WEB_PERSONA } from "fieldpro-tools";
import _ from "lodash";

import { FILTER_TAG } from "components/Filter/TypeFilter";
import { TABLE_COLUMN_TYPE, TColumnType } from "components/Table/model";
import { IOption } from "model/application/components";
import TLang from "model/application/Lang";
import { ITeamSelector } from "model/entities/Team";
import { IMobileUser, IWebUser } from "model/entities/User";
import getFullName from "utils/formatting/getFullName";
import { combineDefaultAndStoredColumns } from "utils/utils";

const BUSINESS_ROLE = "business_role";

type TUser = IWebUser | IMobileUser;

interface IGetFilteredUsers {
  teams: ITeamSelector[];
  users: IWebUser[] | IMobileUser[];
  filterParams: any;
  userType: "mobile" | "web";
  showDebugUser?: boolean;
}

const DEBUG_USER_EMAIL = "debug@optimetriks.com";

export const filterTeamsByLevels = (
  teams: ITeamSelector[],
  hierarchyFilters: string[],
): ITeamSelector[] => {
  return teams.filter((team) => {
    const levels: string[] = [];
    for (let i = 0; i <= 9; i++) {
      // @ts-expect-error TS7053
      const level = team[`level_${i}`];
      if (level && !levels.includes(level)) {
        levels.push(level);
      }
    }
    return hierarchyFilters.some((filter) => levels.includes(filter));
  });
};

export const getPersonaOptions = (langKey?: {
  [key: string]: string;
}): IOption[] => {
  const OPTIONS = [
    PERSONA.SALES_REPRESENTATIVE,
    PERSONA.VAN_SALES,
    PERSONA.DRIVER,
    PERSONA.SUPERVISOR,
  ];
  return _.map(OPTIONS, (persona) => {
    return {
      key: persona,
      label: langKey?.[persona] ?? _.upperFirst(_.lowerCase(persona)),
    };
  });
};

export const getWebPersonaOptions = (): IOption[] => {
  const OPTIONS = _.values(WEB_PERSONA);
  const optionsFormatted = _.map(OPTIONS, (persona) => {
    return {
      key: persona,
      label: _.upperFirst(_.lowerCase(persona)),
    };
  });

  return optionsFormatted;
};

export const getFilteredUsers = ({
  teams,
  users,
  filterParams,
  userType,
  showDebugUser = false,
}: IGetFilteredUsers) => {
  let result = users;
  if (!showDebugUser) {
    result = _.reject<TUser>(result, { email: DEBUG_USER_EMAIL });
  }

  if (
    !Object.keys(filterParams).includes("_id") &&
    !Object.keys(filterParams).includes(FILTER_TAG.TEAMS)
  ) {
    return result;
  }

  _.keys(filterParams).forEach((att) => {
    if (att === "_id") {
      result = _.filter<TUser>(result, (u) => u.id === filterParams["_id"]);
    }

    if (att === "_teams") {
      const teamIds: string[] = filterParams[FILTER_TAG.TEAMS];
      let userIdsInTeams: string[] = [];

      _.forEach(teamIds, (teamId) => {
        const teamSelector = _.find(teams, { id: teamId });
        if (!teamSelector) {
          return;
        }

        const usersInTeam =
          userType === "mobile"
            ? teamSelector.mobile_users
            : teamSelector.web_users;

        if (!usersInTeam) {
          return;
        }

        userIdsInTeams = _.union(
          userIdsInTeams,
          _.map(usersInTeam, (user) => user.key),
        );
      });

      result = _.filter<TUser>(result, (r) => _.includes(userIdsInTeams, r.id));
    }
  });

  return result;
};

/**
 * Creates column titles for table
 * @returns {Array}
 */
export const createMobileUserColumnTypes = (
  lang: TLang,
  showInternalId: boolean,
  showRoles: boolean,
  showPersona: boolean = false,
  _showId: boolean = true,
  storedColumnTypes?: TColumnType[],
): TColumnType[] => {
  const labelToDisplay =
    lang.containers.users.subCategories.mobileUsers.columns;

  const columns: Array<TColumnType> = [
    {
      name: "name",
      label: labelToDisplay.name,
      type: TABLE_COLUMN_TYPE.TEXT,
      hidden: false,
    },
    ...[
      showInternalId
        ? {
            name: "internal_id",
            label: labelToDisplay.internalId,
            type: TABLE_COLUMN_TYPE.TEXT,
            hidden: false,
          }
        : (undefined as any as TColumnType),
    ],
    {
      name: "phone",
      label: labelToDisplay.phone,
      type: TABLE_COLUMN_TYPE.TEXT,
      hidden: false,
    },
    {
      name: "teamNb",
      label: labelToDisplay.teamNb,
      type: TABLE_COLUMN_TYPE.NUMBER,
      hidden: false,
    },
    {
      name: "licensed",
      label: labelToDisplay.license,
      type: TABLE_COLUMN_TYPE.SWITCH,
      hidden: false,
    },
    {
      name: "persona",
      label: labelToDisplay.persona,
      type: TABLE_COLUMN_TYPE.SINGLE_CHOICE,
      hidden: !showPersona,
    },
    ...[
      showRoles
        ? {
            name: "role",
            label: labelToDisplay.role,
            type: TABLE_COLUMN_TYPE.TEXT,
            hidden: false,
          }
        : (undefined as any as TColumnType),
    ],
    {
      name: "appVersion",
      label: labelToDisplay.appVersion,
      type: TABLE_COLUMN_TYPE.TEXT,
      hidden: false,
    },
    {
      name: "lastPullSync",
      label: labelToDisplay.lastPullSync,
      type: TABLE_COLUMN_TYPE.TEXT,
      hidden: false,
    },
    {
      name: "lastPushSync",
      label: labelToDisplay.lastPushSync,
      type: TABLE_COLUMN_TYPE.TEXT,
      hidden: false,
    },

    // Additional hidden columns
    {
      name: "user_id",
      label: "ID",
      type: TABLE_COLUMN_TYPE.TEXT,
      hidden: true,
    },
    {
      name: "official_id",
      label: labelToDisplay.officialId,
      type: TABLE_COLUMN_TYPE.TEXT,
      hidden: true,
    },
    {
      name: "gender",
      label: labelToDisplay.gender,
      type: TABLE_COLUMN_TYPE.TEXT,
      hidden: true,
    },
    {
      name: "picture",
      label: labelToDisplay.picture,
      type: TABLE_COLUMN_TYPE.PICTURE,
      hidden: true,
    },
    {
      name: "id_picture",
      label: labelToDisplay.idPicture,
      type: TABLE_COLUMN_TYPE.PICTURE,
      hidden: true,
    },
  ];

  return combineDefaultAndStoredColumns(columns, storedColumnTypes);
};
export const createMobileUserFilterTypes = (
  lang: TLang,
  showInternalId: boolean,
  showRoles: boolean,
  mobileUsers: IMobileUser[],
): TColumnType[] => {
  const labelToDisplay =
    lang.containers.users.subCategories.mobileUsers.columns;

  const orderLangKey =
    lang.containers.orders.subCategories.orders.createEditModal.persona.options;

  return [
    {
      name: "name",
      label: labelToDisplay.name,
      type: TABLE_COLUMN_TYPE.MULTIPLE_CHOICE,
      options: getNameOptions(mobileUsers),
    },
    ...[
      showInternalId
        ? {
            name: "internalId",
            label: labelToDisplay.internalId,
            type: TABLE_COLUMN_TYPE.TEXT,
          }
        : (undefined as any as TColumnType),
    ],
    {
      name: "phone",
      label: labelToDisplay.phone,
      type: TABLE_COLUMN_TYPE.MULTIPLE_CHOICE,
      options: getOptions(mobileUsers, "phone"),
    },
    {
      name: "teamNb",
      label: labelToDisplay.teamNb,
      type: TABLE_COLUMN_TYPE.NUMBER,
    },
    {
      name: "licensed",
      label: labelToDisplay.license,
      type: TABLE_COLUMN_TYPE.SWITCH,
    },
    {
      name: "persona",
      label: labelToDisplay.persona,
      type: TABLE_COLUMN_TYPE.MULTIPLE_CHOICE,
      options: getPersonaOptions(orderLangKey),
    },

    ...[
      showRoles
        ? {
            name: "role",
            label: labelToDisplay.role,
            type: TABLE_COLUMN_TYPE.MULTIPLE_CHOICE,
            options: getOptions(mobileUsers, "role"),
          }
        : (undefined as any as TColumnType),
    ],
    {
      name: "appVersion",
      label: labelToDisplay.appVersion,
      type: TABLE_COLUMN_TYPE.TEXT,
    },
    {
      name: "lastPullSync",
      label: labelToDisplay.lastPullSync,
      type: TABLE_COLUMN_TYPE.TEXT,
    },
    {
      name: "lastPushSync",
      label: labelToDisplay.lastPushSync,
      type: TABLE_COLUMN_TYPE.TEXT,
    },
  ].filter((e) => e);
};

const checkBusinessRoleOnAllWebUser = (webUsers: IWebUser[]) => {
  let bool = false;
  _.forEach(webUsers, (user) => {
    if (
      // @ts-expect-error TS7053
      user[BUSINESS_ROLE] === null ||
      // @ts-expect-error TS7053
      user[BUSINESS_ROLE] === "" ||
      // @ts-expect-error TS7053
      user[BUSINESS_ROLE] === "no-options" ||
      // @ts-expect-error TS7053
      user[BUSINESS_ROLE] === undefined
    ) {
      bool = true;
    } else {
      bool = false;
    }
  });
  return bool;
};

/**
 * Creates column titles for table
 * @returns {Array}
 */
export const createWebUserColumnTypes = (
  lang: TLang,
  webUsers: IWebUser[],
): TColumnType[] => {
  const langKey = lang.containers.users.subCategories.webUsers;
  const checkBusinessRole: boolean = checkBusinessRoleOnAllWebUser(webUsers);
  return [
    {
      name: "name",
      label: langKey.columns.name,
      type: TABLE_COLUMN_TYPE.TEXT,
    },
    {
      name: "email",
      label: langKey.columns.email,
      type: TABLE_COLUMN_TYPE.TEXT,
    },
    {
      name: "role",
      label: langKey.columns.role,
      type: TABLE_COLUMN_TYPE.TEXT,
    },
    {
      name: "business_role",
      label: langKey.columns.businessRole,
      type: TABLE_COLUMN_TYPE.TEXT,
      hidden: checkBusinessRole,
    },
    {
      name: "last_login",
      label: langKey.columns.lastLogin,
      type: TABLE_COLUMN_TYPE.TEXT,
    },
  ];
};

export const createWebUserFilterTypes = (
  lang: TLang,
  webUsers: IWebUser[],
): TColumnType[] => {
  const langKey = lang.containers.users.subCategories.webUsers;
  return [
    {
      name: "name",
      label: langKey.columns.name,
      type: TABLE_COLUMN_TYPE.MULTIPLE_CHOICE,
      options: getNameOptions(webUsers),
    },
    {
      name: "email",
      label: langKey.columns.email,
      type: TABLE_COLUMN_TYPE.MULTIPLE_CHOICE,
      options: getOptions(webUsers, "email"),
    },
    {
      name: "role",
      label: langKey.columns.role,
      type: TABLE_COLUMN_TYPE.MULTIPLE_CHOICE,
      options: getOptions(webUsers, "role"),
    },
    {
      name: "last_login",
      label: langKey.columns.lastLogin,
      type: TABLE_COLUMN_TYPE.TEXT,
    },
  ];
};

const getOptions = (
  webUsers: IWebUser[],
  field: keyof IWebUser | keyof IMobileUser,
): IOption[] => {
  return _(webUsers)
    .map(field as string)
    .uniq()
    .compact()
    .map((value) => {
      return {
        key: value,
        label: value,
      };
    })
    .value();
};

const getNameOptions = (webUsers: Array<IWebUser | IMobileUser>): IOption[] => {
  return _(webUsers)
    .map((webuser) => {
      const value = webuser.first_name + " " + webuser.last_name;
      return {
        key: value,
        label: value,
      };
    })
    .value();
};

export const getMobileUserName = (
  id: string,
  mobileUsers: IMobileUser[],
): string => {
  const user = _.find(mobileUsers, { id });
  return getFullName(user);
};

export const getRolePersona = (
  role: string,
  profiles: IAccessRightProfile[],
) => {
  const profile = _.find(profiles, (profile) => profile.name === role);
  return profile?.persona ?? PERSONA.SALES_REPRESENTATIVE; // TODO: is this a safe fallback ?
};

export const filterMobileUserPerPersona = (
  mobileUsers: IMobileUser[],
  mobileAccessRightProfiles: IAccessRightProfile[],
  personas: PERSONA[],
): IMobileUser[] => {
  return mobileUsers.filter((user) => {
    const role = _.find(
      mobileAccessRightProfiles,
      (profile) => profile.name === user.role,
    );
    return _.includes(personas, role?.persona);
  });
};
